<template>
    <VJoyWrapper no-margin direction="column" :row-gap="mainRowGap" align="flex-start">
        <VJoyWrapper
            v-if="sectionVisibility[SectionsVisibilityType.DESCRIPTION]"
            class="profile-about-section__wrapper"
            no-margin
            direction="column"
            row-gap="2"
            align="flex-start"
            data-testid="profile-description"
        >
            <VJoyLabel :text="t('profile.section.about.subtitle.description')" :size="isMF ? 'small' : 'medium'"></VJoyLabel>
            <SeeMoreOrRedirect
                v-if="hasDescription"
                :max-lines="shouldDisplayLongDescription ? 20 : 4"
                :content="profileSanitizedDescription"
                :html-content="true"
                :locked="{
                    allowHref: !isMF,
                    label: t('profile.section.description.private', [profile?.firstname]),
                    componentName: 'profile-description-section',
                    targetId: anchorsPageProfile.aboutSection,
                }"
                @see-more-clicked-when-locked="$emit('seeMoreClickedWhenLocked')"
            >
                <!-- eslint-disable vue/no-v-html -->
                <div class="profile-about-section__description" data-testid="profile-description-content" v-html="profileSanitizedDescription" />
                <!-- eslint-enable vue/no-v-html -->
            </SeeMoreOrRedirect>
            <slot v-else name="description-empty-state" />
        </VJoyWrapper>
        <VJoyWrapper
            v-if="sectionVisibility[SectionsVisibilityType.INDUSTRY_EXPERTISE]"
            class="profile-about-section__wrapper"
            no-margin
            direction="column"
            row-gap="2"
            align="flex-start"
            data-testId="profile-about-section__industry-expertises"
        >
            <VJoyLabel :text="t('profile.section.about.subtitle.industry.expertises')" :size="isMF ? 'small' : 'medium'" />
            <SkillsReadOnly
                v-if="hasIndustryExpertises"
                :skills="industryExpertisesAsSkills"
                :is-visible-seo-link="true"
                data-testid="profile-about-section__industry-expertises-list"
            />
            <slot v-else name="industry-expertises-empty-state" />
        </VJoyWrapper>
        <VJoyWrapper
            v-if="sectionVisibility[SectionsVisibilityType.LANGUAGES]"
            class="profile-about-section__wrapper"
            no-margin
            direction="column"
            row-gap="2"
            align="flex-start"
            data-testId="profile-about-section__languages"
        >
            <VJoyLabel :text="t('profile.section.about.subtitle.languages')" :size="isMF ? 'small' : 'medium'" />
            <LanguagesReadOnly v-if="hasLanguageItems" :languages="languageItems" />
            <slot v-else name="languages-empty-state" />
        </VJoyWrapper>
        <VJoyWrapper
            v-if="!isMF"
            class="profile-about-section__wrapper"
            no-margin
            direction="column"
            row-gap="2"
            align="flex-start"
            data-testId="profile-about-section__workplace-preferences"
        >
            <VJoyLabel :text="t('profile.section.about.subtitle.workplace.preferences')" :size="isMF ? 'small' : 'medium'" />
            <WorkplacePreferencesReadOnly :workplace-preferences="workplacePreferences" :is-strategy-profile="isStrategyProfile" />
        </VJoyWrapper>
    </VJoyWrapper>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useMarkdownToHtml, useTranslation} from '#imports';
    import {VJoyLabel, VJoyWrapper} from '@maltjoy/core-vue';
    import {toLanguageItems} from '~/components/_common/language/LanguageItem';
    import LanguagesReadOnly from '~/components/_common/language/LanguagesReadOnly.vue';
    import {toWorkplacePreferences} from '~/components/_common/location-and-workplace-preferences/WorkplacePreferences';
    import WorkplacePreferencesReadOnly from '~/components/_common/location-and-workplace-preferences/WorkplacePreferencesReadOnly.vue';
    import SeeMoreOrRedirect from '~/components/_common/see-more/SeeMoreOrRedirect.vue';
    import SkillsReadOnly from '~/components/_common/skills/SkillsReadOnly.vue';
    import {anchorsPageProfile} from '~/config/anchors';
    import {overrideH1ToH4} from '~/config/mdToHtmlOverrides';
    import {useProfileStore} from '~/store/profileStore';
    import {useSectionsVisibilityStore} from '~/store/sectionsVisibility';
    import {SectionsVisibilityType} from '~/types/domain/SectionsVisibility';
    import {useStrategyProfile} from '@/composables/useStrategyProfile';

    const profileStore = useProfileStore();
    const {industryExpertises, profile, profileLocationPreferences} = storeToRefs(profileStore);
    const {sectionVisibility} = storeToRefs(useSectionsVisibilityStore());

    export type DrawerMode = 'PRE_APPLICATION' | 'POST_APPLICATION';
    const props = defineProps<{
        mfDrawerMode?: DrawerMode;
    }>();

    const emits = defineEmits(['seeMoreClickedWhenLocked']);

    // Composables
    const {t} = useTranslation();
    const {markdownToHtml} = useMarkdownToHtml();

    // Computed values
    const isMF = computed(() => !!props.mfDrawerMode);
    const shouldDisplayLongDescription = computed(() => props.mfDrawerMode === 'PRE_APPLICATION');
    const mainRowGap = computed(() => (isMF.value ? '4' : '6'));
    const industryExpertisesAsSkills = computed(
        () =>
            industryExpertises.value?.map((industryExpertise) => ({
                id: industryExpertise.id,
                label: industryExpertise.label,
                seoUrl: industryExpertise.seoUrl,
            })) ?? [],
    );
    const languageItems = computed(() => (profile.value?.languages ? toLanguageItems(profile.value!.languages) : []));
    const hasDescription = computed(() => (profile.value?.description?.length ?? 0) > 0);
    const hasIndustryExpertises = computed(() => (industryExpertises.value?.length ?? 0) > 0);
    const hasLanguageItems = computed(() => languageItems.value.length > 0);

    const isStrategyProfile = computed(() => useStrategyProfile() || false);
    const profileSanitizedDescription = computed(() => markdownToHtml(profile?.value?.description, overrideH1ToH4));
    const workplacePreferences = computed(() => toWorkplacePreferences(profileLocationPreferences.value!));
</script>
<style scoped lang="scss">
    .profile-about-section {
        &__wrapper {
            :deep(.joy-label__group) {
                margin-bottom: 0;
            }
        }

        &__description {
            word-break: normal;
            overflow-wrap: anywhere;
            text-align: left;
        }
    }
</style>
